import { router } from "..";
import { getFarmcamSim } from "../api/farmcam-sims_{imsi}";
import { type Price, prices } from "../billing/prices";
import { deviceCard, formatSubscriptionStatus } from "../device/device_card";
import { t } from "../locale";
import { addNotificationListener } from "../mobile";
import { state } from "../state";
import { formatDataAmount } from "../utils";
import { keyValueParagraph } from "../view/components";
import { Html } from "../view/html";
import { MaterialDesign } from "../view/material_design";
import { getFarmcamSimWarnings } from "./fcs_warnings";

const farmcamSimCard = async (fcs: FarmcamSim): Promise<HTMLDivElement> => {
	addNotificationListener("farmcamsim", fcs.Imsi, () => {
		getFarmcamSim(fcs.Imsi).then((fcs) => {
			state.farmcamSims[fcs.Imsi] = fcs;
		});
	});

	return deviceCard({
		deviceName: fcs.Metadata.Name,
		deviceType: "farmcam-sim",
		productName: "FarmCam SIM",
		warnings: getFarmcamSimWarnings(fcs),
		lines: [
			keyValueParagraph(
				t.farmcamSim.remainingDataLabel,
				formatDataAmount(fcs.RemainingData),
			),
			keyValueParagraph(t.device.subscriptionLabel),
			keyValueParagraph(
				`- ${t.device.card.planLabel}`,
				await formatSubscriptionPlan(fcs),
			),
			keyValueParagraph(...formatSubscriptionStatus(fcs.Subscription)),
		],
		buttons: [
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "monitoring",
					onClick: () => router.jump(`/farmcam-sim/${fcs.Imsi}/logs`),
				}),
			),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "settings",
					onClick: () => router.jump(`/farmcam-sim/${fcs.Imsi}`),
				}),
			),
		],
	});
};

/**
 * @param {FarmcamSim} fcs
 */
const formatSubscriptionPlan = async (fcs: FarmcamSim): Promise<string> => {
	if (fcs.Subscription.Trialing) return t.trial;

	const { FarmcamSimLarge, FarmcamSimMedium, FarmcamSimSmall } = await prices;

	const formatPrice = (price: Price): string => {
		const matches = price.Name.match(/([0-9]+ [KMGT]?B)/gm);
		if (!matches)
			throw new Error(`invalid subscription name for price: ${price.Id}`);
		return matches[0];
	};

	switch (fcs.Subscription.PriceIds.Current) {
		case FarmcamSimLarge.Id:
			return formatPrice(FarmcamSimLarge);
		case FarmcamSimMedium.Id:
			return formatPrice(FarmcamSimMedium);
		case FarmcamSimSmall.Id:
			return formatPrice(FarmcamSimSmall);
	}

	// This is to handle the transition period from priceId to priceIds.current/next
	const expiryTime = new Date(fcs.Subscription.ExpiresAt);
	if (Date.now() < expiryTime.getTime()) return "";

	return t.none;
};

export { farmcamSimCard };
